import Box from "@mui/material/Box";
import {ColDef, ICellRendererParams, IServerSideGetRowsParams,} from "ag-grid-community";
import * as React from "react";
import {useContext, useEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import SectionHeader from "../../../../util/component/SectionHeader";
import {AgGridReact} from "ag-grid-react";
import {
    FormLocationGroupModel,
    FormSearchLocationGroupModel,
    LocationGroupEntityRequest,
    LocationGroupEntityResponse,
} from "../../api/types/locationGroup";
import {useLocationGroupRepository} from "../../api/repositories/LocationGroupRepository";
import {onGridReady, useActionColDef} from "../../utils";
import {DEFAULT_COL_DEF, SET_SNACKBAR} from "../../constants";
import {Button, Dialog, DialogActions, DialogContent, Grid, useTheme,} from "@mui/material";
import {useAgGridLocale} from "../../../../config/globalConfig";
import SearchFilters from "../../common/components/SearchFilters";
import {
    DialogInterface,
    FormDialog,
    GenericComponentModel,
    SelectElementModelBE,
    SelectElementModelFE,
} from "../../api/types/generic";
import * as yup from "yup";
import { FormAutocomplete, FormTextField } from "../../common/form-fields";
import {GlobalContext} from "../../../../util/state/GlobalContextProvider";
import Loading from "../../common/components/Loading";
import AgGridCustomTable from "../../../../util/component/AgGridCustomTable";
import {FormProvider, useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import BasicToolbar from "../../common/components/BasicToolbar";
import {useMutation} from "@tanstack/react-query";
import ActionsPopover from "../../common/components/ActionsPopover";
import DialogComponent from "../../common/components/DialogComponent";
import BootstrapDialogTitle from "../../common/components/BootstrapDialogTitle";
import DealerAndTenant from "../../common/components/DealerAndTenant";

const LocationGroup = ({readOnly}: { readOnly: boolean }) => {
    // Use Translation
    const {t} = useTranslation(["translation", "pricing"]);

    // Use Theme
    const theme = useTheme();
    // Use Context
    const {
        dispatchGlobalData,
        globalData: {user},
    } = useContext(GlobalContext);

    //setto tutte le legalEntities options e le passo al filtro di ricerca ["all", "drivalia_it","drivalia_fr"]
    const legalEntityOptions: SelectElementModelFE[] = user.legalEntities?.map(
      (el: SelectElementModelBE) => ({
          code: el.name,
          label: t('translation:legalEntities.'.concat(el.name))
      })
    );

    //se utente clicca su all, mando  ["drivalia_it","drivalia_fr"]
    const optionsFiltered = legalEntityOptions
      .filter(function (el) {
          return el.code !== "all";
      });

    // Use State
    const [dialogLegalEntity, setDialogLegalEntity] = useState(optionsFiltered.length === 1 ? optionsFiltered[0] : null);
    const [formDialog, setFormDialog] = useState<FormDialog | null>(null);
    const [dialog, setDialog] = useState<DialogInterface | null>(null);
    const [initialValues, setInitialValues] = useState<FormLocationGroupModel>({
        name: "",
        legalEntity: optionsFiltered.length === 1 ? optionsFiltered[0] : null,
    });
    const customSort = "LocationGroupEntityResponse";

    // Use Repository Hooks
    const {
        postLocationGroup,
        putLocationGroup,
        deleteLocationGroup,
        getLocationGroupsPaged,
    } = useLocationGroupRepository();

    // Use Mutation
    const mutationPostLocationGroup = useMutation({
        mutationFn: (locationGroupEntityRequest: LocationGroupEntityRequest) =>
            postLocationGroup(locationGroupEntityRequest),
        onSuccess: () => {
            handleGetLocationGroupsPage();
        },
    });
    const mutationPutLocationGroup = useMutation({
        mutationFn: (locationGroupEntityResponse: LocationGroupEntityResponse) =>
            putLocationGroup(locationGroupEntityResponse),
        onSuccess: () => {
            handleGetLocationGroupsPage();
        },
    });
    const mutationDeleteLocationGroup = useMutation({
        mutationFn: (locationGroupId: number) =>
            deleteLocationGroup(locationGroupId),
        onSuccess: () => {
            handleGetLocationGroupsPage();
        },
    });

    // Use AgGrid Locale
    const agGridLocale = useAgGridLocale();

    const everyLegalEntitiesWithoutAll = legalEntityOptions
        .map((el) => el.code as string)
        .filter((el) => el !== "all");

    const [testFilters, setTestFilters] = useState<FormSearchLocationGroupModel>({
        name: "",
        legalEntities: everyLegalEntitiesWithoutAll
    })

    // Use Ref
    const gridRef = useRef<AgGridReact>(null);

    const handleGetLocationGroupsPage = () => {
        gridRef.current!.api.refreshServerSide({purge: true});
    };

    const handleTriggerFilters = (entries: FormSearchLocationGroupModel) => {
        const hardcodedFilter = Object.entries(entries).reduce(
            (acc, [key, value]) => ({
                ...acc,
                [key]: {type: "contains", filter: value},
            }),
            {}
        );

        gridRef.current?.api.paginationGoToFirstPage();
        gridRef.current?.api.setFilterModel(hardcodedFilter);
        gridRef.current?.api.onFilterChanged();
    };

    useEffect(() => {
        if (gridRef?.current) {
            handleTriggerFilters({
                name: testFilters.name as string,
                legalEntities:
                    ((testFilters.legalEntities as SelectElementModelFE)
                        ?.code as string) === "all"
                        ? everyLegalEntitiesWithoutAll
                        : ((testFilters.legalEntities as SelectElementModelFE[]).map(el => el.code as string)),
            });
        }
    }, []);

    useEffect(() => {
        handleTriggerResetFilters()
    }, [])

    const handleTriggerResetFilters = () => {
        setTestFilters({name: "", legalEntities: []});
        handleTriggerFilters({name: "", legalEntities: []});
    };

    const handleTriggerFiltersInitial = () => {
        handleTriggerFilters({
            name: testFilters.name as string,
            legalEntities:
                ((testFilters.legalEntities as SelectElementModelFE)
                    ?.code as string) === "all"
                    ? everyLegalEntitiesWithoutAll
                    : ((testFilters.legalEntities as SelectElementModelFE[]).map(el => el.code as string)),
        });
    };

    // ************ FORM - START ************ //
    const validationSchema = yup
        .object()
        .shape({
            name: yup
                .string()
                .trim()
                .lowercase()
                .required(t("translation:validation.fieldMandatory"))
                .min(2),
            legalEntity: yup
                .object()
                .shape({
                    code: yup.string(),
                    label: yup.string(),
                })
                .nullable()
                .required(t("translation:validation.fieldMandatory")),
        })
        .defined();

    // Use Form
    const form = useForm<FormLocationGroupModel>({
        defaultValues: initialValues,
        resolver: yupResolver(
            validationSchema,
            {},
            {
                rawValues: true,
            }
        ),
        shouldUnregister: true,
    });
    // ************ FORM - END ************ //

    // ************ AG GRID START ************ //
    const handleClickViewLocationGroup = (
        locationGroup: LocationGroupEntityResponse
    ) => {
        if (locationGroup) {
            const result = {
                id: locationGroup.id,
                name: locationGroup.name,
                legalEntity: {code: locationGroup.legalEntity, label: locationGroup.legalEntity},
            };
            form.reset(result); // asynchronously reset your form values
            setDialogLegalEntity({code: locationGroup.legalEntity, label: t("translation:legalEntities." + locationGroup.legalEntity)});
            setFormDialog({
                open: true,
                onClickConfirm: form.handleSubmit(handleConfirmSave),
                readonly: true,
            });
        }
    };

    const handleClickUpdateLocationGroup = (
        locationGroupEntityResponse: LocationGroupEntityResponse
    ) => {
        if (locationGroupEntityResponse) {
            form.setValue("id", locationGroupEntityResponse.id);
            form.setValue("name", locationGroupEntityResponse.name);
            form.setValue("legalEntity", {
                code: locationGroupEntityResponse.legalEntity,
                label: locationGroupEntityResponse.legalEntity,
            });
            setDialogLegalEntity({code: locationGroupEntityResponse.legalEntity, label: t("translation:legalEntities." + locationGroupEntityResponse.legalEntity)});
            setFormDialog({
                open: true,
                onClickConfirm: form.handleSubmit(handleConfirmSave),
                readonly: false,
            });
        }
    };

    const handleConfirmSave = async (values: FormLocationGroupModel) => {
        const newLocationGroup: LocationGroupEntityRequest = {
            name: values.name,
            legalEntity: legalEntityOptions.length === 1 ? everyLegalEntitiesWithoutAll[0] : values.legalEntity?.code as string,
        };

        try {
            values?.id
                ? await mutationPutLocationGroup.mutate({
                    id: values?.id,
                    ...JSON.parse(JSON.stringify(newLocationGroup)),
                })
                : await mutationPostLocationGroup.mutate(newLocationGroup);

            // reset form
            setFormDialog(null);
            setDialogLegalEntity(null);
            form.reset(initialValues);
            dispatchGlobalData(SET_SNACKBAR.SUCCESS);
        } catch (e) {
        }
    };

    const handleConfirmDelete = async (deletingRowIndex: number) => {
        try {
            await mutationDeleteLocationGroup.mutate(deletingRowIndex);
            setDialog(null);
            form.reset(initialValues);
            dispatchGlobalData(SET_SNACKBAR.SUCCESS);

            // get lista rental period
            handleGetLocationGroupsPage();
        } catch (e) {
        }
    };

    const viewButtonClicked = (data: LocationGroupEntityResponse) => {
        handleClickViewLocationGroup(data);
    };

    const updateButtonClicked = (data: LocationGroupEntityResponse) => {
        handleClickUpdateLocationGroup(data);
    };

    const deleteButtonClicked = (data: LocationGroupEntityResponse) => {
        setDialog({
            open: true,
            message: t("pricing:locations.dialogContents.removeGroupOfLocation"),
            onClickConfirm: () => handleConfirmDelete(data.id),
        });
    };

    const {actionColDef} = useActionColDef();
    const columnDefs: ColDef[] = [
        {
            headerName: t("pricing:locations.table.headers.locationGroup"),
            headerTooltip: t("pricing:locations.table.headers.locationGroup"),
            field: "name",
            editable: false,
            flex: 1,
            filter: "text",
        },
        {
            headerName: t("pricing:locations.table.headers.legalEntities"),
            headerTooltip: t("pricing:locations.table.headers.legalEntities"),
            field: "legalEntities",
            editable: false,
            flex: 1,
            filter: "text",
            cellRenderer: (params: ICellRendererParams) => t('translation:legalEntities.'.concat(params.data.legalEntity)),
            hide: user.legalEntities.length != 1 ? false : true
        },
        {
            ...actionColDef,
            cellRenderer: (params: ICellRendererParams) => (
                <ActionsPopover
                    params={params}
                    view={{
                        action: viewButtonClicked,
                        userHasAuth: !!user.profiles.find((profile: any) => {
                            return profile.auths.find((auth: any) => {
                                return auth.name === "SCOPE_PRC_VIEW_LOCATION_GROUP";
                            });
                        }),
                    }}
                    update={{
                        action: updateButtonClicked,
                        userHasAuth: !!user.profiles.find((profile: any) => {
                            return profile.auths.find((auth: any) => {
                                return auth.name === "SCOPE_PRC_UPDATE_LOCATION_GROUP";
                            });
                        }),
                    }}
                    del={{
                        action: deleteButtonClicked,
                        userHasAuth: !!user.profiles.find((profile: any) => {
                            return profile.auths.find((auth: any) => {
                                return auth.name === "SCOPE_PRC_DELETE_LOCATION_GROUP";
                            });
                        }),
                    }}
                />
            ),
        },
    ];

    const defaultColDef = DEFAULT_COL_DEF;

    const createDatasource = () => {
        return {
            getRows: async (params: IServerSideGetRowsParams) => {
                let sort =
                    params.request.sortModel.length === 0
                        ? ""
                        : params.request.sortModel[0].colId;
                let order =
                    params.request.sortModel.length === 0
                        ? ""
                        : params.request.sortModel[0].sort;

                const {filterModel} = params.request;
                const filter: FormSearchLocationGroupModel = Object.entries(
                    filterModel as Record<string, { filter?: string; type: string }>
                ).reduce(
                    (acc, [key, value]) => ({
                        ...acc,
                        [key]: value?.filter || "",
                    }),
                    testFilters
                );

                function getCustomSort() {
                    let currentSort = sort;
                    switch (customSort) {
                        case "LocationGroupEntityResponse": {
                            switch (sort) {
                                case "legalEntities": {
                                    currentSort = "legalEntity";
                                    break
                                }
                            }
                            break;
                        }
                    }

                    return currentSort;
                }

                const locationGroupsPaged = await getLocationGroupsPaged({
                    ...filter,
                    //sort: sort && order ? `${sort},${order}` : "",
                    sort: sort && order ? `${customSort ? getCustomSort() : sort},${order}` : "",
                    size: gridRef?.current?.api?.paginationGetPageSize() || 10,
                    page: gridRef?.current?.api?.paginationGetCurrentPage() || 0,
                });
                if (locationGroupsPaged.totalElements === 0) {
                    gridRef.current!.api.showNoRowsOverlay();
                } else {
                    gridRef.current!.api.hideOverlay();
                }

                params.success({
                    rowData: locationGroupsPaged.content,
                    rowCount: locationGroupsPaged.totalElements,
                });
            },
        };
    };
    // ************ AG GRID END ************ //

    const filtersOutOfCollapse: GenericComponentModel[] = [
        {
            name: "name",
            value: testFilters.name,
            label: `${t("pricing:locations.form.labels.name")}`,
            handleChange: (value: string) =>
                setTestFilters({...testFilters, name: value}),
            options: [],
            type: "TextField",
        },
        {
            name: "legalEntity",
            value: testFilters.legalEntities as SelectElementModelFE,
            label: `${t("pricing:locations.form.labels.legalEntity")}`,
            handleChange: (value: SelectElementModelFE) =>
                setTestFilters({...testFilters, legalEntities: value}),
            options: legalEntityOptions || [],
            type: "AutocompleteM",
            hide: user.legalEntities.length != 1 ? false : true
        }
    ];
    const filtersInCollapse: GenericComponentModel[] = [];

    const toolbar = {
        renderActions: () => (
            <BasicToolbar
                addIcon={
                    !!user.profiles.find((profile: any) => {
                        return profile.auths.find((auth: any) => {
                            return auth.name === "SCOPE_PRC_CREATE_LOCATION_GROUP";
                        });
                    })
                        ? {
                            onClick: () =>
                                setFormDialog({
                                    open: true,
                                    onClickConfirm: form.handleSubmit(handleConfirmSave),
                                    readonly: false,
                                }),
                        }
                        : undefined
                }
            />
        ),
    };

    return (
        <Box>
            <SectionHeader
                title={t("translation:auths.SCOPE_PRC_VIEW_LOCATION_GROUP.name")}
            />
                <Dialog open={!!formDialog?.open} maxWidth={"lg"} fullWidth>
                    <BootstrapDialogTitle
                        id="customized-dialog-title"
                        onClose={() => {
                            form.reset(initialValues);
                            setFormDialog(null);
                            setDialogLegalEntity(null);
                        }}
                    >
                        <Box sx={{display: "flex", alignItems: "center"}}>
                            {t("pricing:locations.section.titles.locationGroup")}
                        </Box>
                    </BootstrapDialogTitle>
                    <DialogContent style={{color: theme.palette.primary.main}}>
                         <FormProvider {...form}>
                            <Grid container spacing={2} alignItems="start">
                                {/* NAME */}
                                <Grid item xs={12} md={3}>
                                    <FormTextField
                                      {...form.register("name")}
                                      label={`${t("pricing:rentalPeriods.form.labels.name")}*`}
                                      readOnly={Boolean(formDialog?.readonly)}
                                    />
                                </Grid>
                                {legalEntityOptions.length === 1 ? null : (
                                  <Grid item xs={12} md={3}>
                                      <FormAutocomplete
                                        // @ts-ignore
                                        name={"legalEntityDialog"}
                                        value={dialogLegalEntity}
                                        inputValue={dialogLegalEntity?.label}
                                        label={`${t("pricing:dealer.tenant")}*`}
                                        options={optionsFiltered}
                                        isOptionEqualToValue={(
                                          option: SelectElementModelFE,
                                          value: SelectElementModelFE
                                        ) => option.code === value.code}
                                        handleChange={(value: SelectElementModelFE) => {form.setValue("legalEntity", value);}}
                                        readOnly={readOnly || Boolean(form.getValues("id"))}
                                      />
                                  </Grid>
                                )}

                            </Grid>
                        </FormProvider>
                    </DialogContent>
                    {!formDialog?.readonly && (
                        <DialogActions
                            style={{
                                backgroundColor: theme.palette.neutral.bright,
                                padding: "1rem",
                            }}
                        >
                            <Button
                                variant="outlined"
                                color={"accent"}
                                onClick={() => {
                                    setFormDialog(null);
                                    form.reset(initialValues);
                                }}
                            >
                                {t("pricing:actions.cancel")}
                            </Button>
                            <Button
                                sx={{marginLeft: "16px"}}
                                autoFocus
                                color={"accent"}
                                onClick={formDialog?.onClickConfirm}
                            >
                                {t("pricing:actions.confirm")}
                            </Button>
                        </DialogActions>
                    )}
                </Dialog>
            <SearchFilters
                filtersOutOfCollapse={filtersOutOfCollapse}
                filtersInCollapse={filtersInCollapse}
                handleClickSearch={handleTriggerFiltersInitial}
                handleClickResetSearch={handleTriggerResetFilters}
            />
            <Grid container p={2}>
                <Grid item xs={12}>
                    <AgGridCustomTable
                        gridRef={gridRef}
                        columnDefs={columnDefs}
                        defaultColDef={defaultColDef}
                        tooltipShowDelay={0}
                        toolbar={toolbar}
                        pagination
                        localeText={agGridLocale}
                        domLayout="autoHeight"
                        rowModelType="serverSide"
                        paginationPageSize={10}
                        cacheBlockSize={10}
                        serverSideInfiniteScroll
                        onGridReady={() =>
                            onGridReady({
                                initialSortColId: "name",
                                gridRef,
                                createDatasource,
                            })
                        }
                    />
                </Grid>
            </Grid>
            <Loading/>

            {dialog?.open && (
                <DialogComponent
                    type={"info"}
                    onClickConfirm={dialog.onClickConfirm}
                    setOpen={() => {
                        setDialog(null);
                        form.reset(initialValues);
                    }}
                    open={dialog.open}
                    message={dialog.message}
                />
            )}
        </Box>
    );
};

export default LocationGroup;
